import { ref, uploadBytes, getDownloadURL, listAll, deleteObject, StorageReference } from 'firebase/storage';
import { storage } from '../config/firebase';

export interface UploadedImage {
  name: string;
  url: string;
  fullPath: string;
}

class StorageService {
  /**
   * Upload images to a specific folder in Firebase Storage
   */
  async uploadImages(files: File[], folderPath: string): Promise<UploadedImage[]> {
    const uploadPromises = files.map(async (file) => {
      // Create a unique filename with timestamp
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      
      const imageRef = ref(storage, `${folderPath}/${fileName}`);
      
      // Upload the file
      await uploadBytes(imageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(imageRef);
      
      return {
        name: fileName,
        url: downloadURL,
        fullPath: imageRef.fullPath
      };
    });

    return Promise.all(uploadPromises);
  }

  /**
   * Get all images from a specific folder
   */
  async getImagesFromFolder(folderPath: string): Promise<UploadedImage[]> {
    try {
      const folderRef = ref(storage, folderPath);
      const result = await listAll(folderRef);
      
      const imagePromises = result.items.map(async (item) => {
        const url = await getDownloadURL(item);
        return {
          name: item.name,
          url,
          fullPath: item.fullPath
        };
      });

      return Promise.all(imagePromises);
    } catch (error: any) {
      console.error('Error loading images from folder:', error);
      // Return empty array for non-existent folders
      if (error.code === 'storage/object-not-found') {
        return [];
      }
      throw error;
    }
  }

  /**
   * Delete an image from storage
   */
  async deleteImage(imagePath: string): Promise<void> {
    const imageRef = ref(storage, imagePath);
    await deleteObject(imageRef);
  }

  /**
   * Get homepage section folder path
   */
  getHomepageSectionFolderPath(sectionId: string): string {
    const safeSectionId = sectionId.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    return `homepage-sections/${safeSectionId}`;
  }

  /**
   * Get hero section folder path
   */
  getHeroSectionFolderPath(): string {
    return 'homepage-sections/hero-section';
  }

  /**
   * Get promotional banner folder path
   */
  getPromotionalBannerFolderPath(): string {
    return 'homepage-sections/promotional-banner';
  }

  /**
   * Get product images folder path
   */
  getProductImagesFolderPath(productId: string): string {
    const safeProductId = productId.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    return `products/${safeProductId}`;
  }

  /**
   * Get collection images folder path
   */
  getCollectionImagesFolderPath(collectionId: string): string {
    const safeCollectionId = collectionId.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    return `collections/${safeCollectionId}`;
  }
}

export const storageService = new StorageService();
